import React, { useMemo } from 'react';
import { Card, Typography, Skeleton } from 'antd';
import { ResponsiveContainer, AreaChart as RAreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts';
import { useI18n, getLocale } from '../i18n';

const { Title } = Typography;

const AreaChart = ({ titleKey, data = [], monthlyStats = [], dataKey = 'Total', unitType = 'people', color = '#1677ff', isLoading = false, showTotal = true }) => {
  const { lang, t } = useI18n();
  const locale = getLocale(lang);
  const unit = unitType === 'people' ? t('peopleUnit') : unitType === 'usd' ? t('usdUnit') : '';
    console.log(titleKey, monthlyStats);
  // 전체 기간 총합과 평균 계산
  const { periodTotal, periodAverage } = useMemo(() => {
    if (!data || data.length === 0) return { periodTotal: 0, periodAverage: 0 };
    const values = data.map(item => item[dataKey] || 0);
    const total = values.reduce((acc, val) => acc + val, 0);
    const average = total / values.length;
    return { periodTotal: total, periodAverage: average };
  }, [data, dataKey]);

  const formattedPeriodTotal = useMemo(() =>
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: unitType === 'usd' ? 2 : 0,
      maximumFractionDigits: unitType === 'usd' ? 2 : 0
    }).format(periodTotal),
    [locale, periodTotal, unitType]
  );

  const formattedPeriodAverage = useMemo(() =>
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: unitType === 'usd' ? 2 : 0,
      maximumFractionDigits: unitType === 'usd' ? 2 : 0
    }).format(periodAverage),
    [locale, periodAverage, unitType]
  );

  return (
    <Card style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <Title level={4} style={{ marginBottom: 16 }}>{t(titleKey)} {t('chart')} ({t(unit)})</Title>
      <div style={{ height: 340 }}>
        {isLoading ? (
          <Skeleton active style={{ width: '100%', height: '100%' }} paragraph={{ rows: 8 }} />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <RAreaChart data={data} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
              <defs>
                <linearGradient id="colorArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="keyName" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => new Intl.NumberFormat(locale).format(v)} />
              <Tooltip formatter={(value) => new Intl.NumberFormat(locale).format(value)} />
              <Area type="monotone" dataKey={dataKey} stroke={color} fillOpacity={1} fill="url(#colorArea)" />
              {data.length > 0 && periodAverage > 0 && (
                <ReferenceLine
                  y={periodAverage}
                  stroke={color}
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{
                    value: `${t('average')}: ${formattedPeriodAverage} ${unit}`,
                    position: 'insideTopRight',
                    fill: color,
                    fontSize: 12,
                    fontWeight: 'bold'
                  }}
                />
              )}
            </RAreaChart>
          </ResponsiveContainer>
        )}
      </div>
      {!isLoading && data.length > 0 && (
        <div style={{
          marginTop: 16,
          padding: '12px 16px',
          background: `${color}08`,
          borderRadius: 8,
          border: `1px solid ${color}20`,
          display: 'flex',
          justifyContent: showTotal ? 'space-between' : 'center',
          alignItems: 'center'
        }}>
          {showTotal && (
            <div>
              <div style={{ fontSize: 12, color: '#00000073', marginBottom: 4 }}>{t('total')}</div>
              <div style={{ fontSize: 18, fontWeight: 'bold', color: color }}>
                {formattedPeriodTotal} {unit}
              </div>
            </div>
          )}
          <div style={{ textAlign: showTotal ? 'right' : 'center' }}>
            <div style={{ fontSize: 12, color: '#00000073', marginBottom: 4 }}>{t('average')}</div>
            <div style={{ fontSize: 18, fontWeight: 'bold', color: color }}>
              {formattedPeriodAverage} {unit}
            </div>
          </div>
        </div>
      )}
      {!isLoading && monthlyStats && monthlyStats.length > 0 && (
        <div style={{
          marginTop: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }}>
          {monthlyStats.map((stat) => {
            const formattedMonth = lang === 'ja'
              ? `${stat.month.substring(0, 4)}年${parseInt(stat.month.substring(5))}月`
              : `${stat.month.substring(0, 4)}년 ${parseInt(stat.month.substring(5))}월`;

            const formattedTotal = new Intl.NumberFormat(locale, {
              minimumFractionDigits: unitType === 'usd' ? 2 : 0,
              maximumFractionDigits: unitType === 'usd' ? 2 : 0
            }).format(stat.total);

            const formattedAverage = new Intl.NumberFormat(locale, {
              minimumFractionDigits: unitType === 'usd' ? 2 : 0,
              maximumFractionDigits: unitType === 'usd' ? 2 : 0
            }).format(stat.average);

            return (
              <div
                key={stat.month}
                style={{
                  padding: '12px 16px',
                  background: `${color}08`,
                  borderRadius: 8,
                  border: `1px solid ${color}20`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 'bold', color: '#000000d9' }}>
                  {formattedMonth}
                </div>
                <div style={{ display: 'flex', gap: 24 }}>
                  {showTotal && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, color: '#00000073', marginBottom: 2 }}>{t('monthTotal')}</div>
                      <div style={{ fontSize: 16, fontWeight: 'bold', color: color }}>
                        {formattedTotal} {unit}
                      </div>
                    </div>
                  )}
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, color: '#00000073', marginBottom: 2 }}>{t('monthAverage')}</div>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: color }}>
                      {formattedAverage} {unit}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default AreaChart;
